import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  Code2,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Printer,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import EncounterForm from "@/components/encounter/EncounterForm";
import EncounterTable from "@/components/patient/EncounterTable";
import EncountersWorkspace from "@/components/patient/EncountersWorkspace";
import AppointmentsWorkspace from "@/components/patient/AppointmentsWorkspace";
import MedicalHistoryWorkspace from "@/components/patient/MedicalHistoryWorkspace";
import PatientSmsConsentCard from "@/components/patient/PatientSmsConsentCard";
import AllergiesWorkspace from "@/components/patient/AllergiesWorkspace";
import SurgicalHistoryWorkspace from "@/components/patient/SurgicalHistoryWorkspace";
import NotesWorkspace from "@/components/patient/NotesWorkspace";
import {
  AllergiesCard,
  MedicalHistoryCard,
  ProblemListCard,
  SurgicalHistoryCard,
} from "@/components/patient/PatientSummaryCards";
import ProblemListWorkspace from "@/components/patient/ProblemListWorkspace";
import PatientForm from "@/components/patient/PatientForm";
import RecentEncountersActions from "@/components/patient/RecentEncountersActions";
import {
  BillingSubtabs,
  ChartDomainDividers,
  ClinicalSubtabs,
  FrontDeskSubtabs,
} from "@/components/patient/ChartFolderNav";
import FhirViewer from "@/components/organization/FhirViewer";
import { PAT } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import {
  NA,
  chartPatientFields,
  fetchPatient,
  fetchPatientEncounters,
  formatDate,
  sortEncountersNewest,
} from "@/lib/patientChart";
import {
  BILLING_TABS,
  CLINICAL_TABS,
  FRONT_DESK_TABS,
} from "@/lib/patientChartClinical";
import { encounterScreenPath } from "@/lib/patientChartNewEncounter";
import { filterTabsByAccess, firstAllowedTab, cycleVisibleTab, canWriteSubtab, canAccessSubtab } from "@/lib/subtabAccess";
import { canWriteMainTab, canAccessMainTab } from "@/lib/maintabAccess";
import {
  loadSummaryAllergies,
  loadSummaryMedicalHistory,
  loadSummaryProblemList,
  loadSummarySurgicalHistory,
} from "@/lib/patientSummary";

const RESULT_TYPES = [
  "Blood",
  "Urine",
  "Microbiology",
  "Biochemistry",
  "Radiology",
  "Pathology",
  "Other",
];

function val(value) {
  if (value == null || value === "") return NA;
  return value;
}

function Field({ label, children, testId }) {
  return (
    <div className="emr-chart-field">
      <dt className="shrink-0">{label}</dt>
      <dd data-testid={testId}>
        {children ?? NA}
      </dd>
    </div>
  );
}

function Card({ title, action, children, testId, className = "" }) {
  return (
    <section
      data-testid={testId}
      className={`emr-chart-card ${className}`.trim()}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="emr-chart-card__title">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function TextLink({ onClick, testId, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="emr-chart-link text-xs font-medium hover:underline"
    >
      {children}
    </button>
  );
}

function EmptyHint({ children }) {
  return <p className="emr-chart-empty">{children}</p>;
}

function PlaceholderPanel({ title, detail, testId }) {
  return (
    <div
      data-testid={testId}
      className="emr-chart-card px-6 py-14 text-center"
    >
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="mt-1 text-sm text-slate-500">{detail}</p>
    </div>
  );
}

export function ClinicalSummary({
  fields,
  problemList,
  medicalHistory,
  surgicalHistory,
  allergies,
  encounters,
  onOpenFrontDesk,
  onOpenEncounters,
  onNewEncounter,
  onOpenResults,
  onOpenMedicalHistory,
  onOpenSurgicalHistory,
  onOpenAllergies,
  onOpenProblemList,
  onSelectEncounter,
}) {
  return (
    <div className="emr-chart-grid">
      <div className="emr-chart-col">
        {onOpenProblemList ? (
        <ProblemListCard items={problemList} onViewAll={onOpenProblemList} />
        ) : null}

        <Card title="Medications (Active)" testId={PAT.chartComingSoonMedications}>
          <EmptyHint>No patient-specific medications are available yet.</EmptyHint>
          <p data-testid={PAT.chartFactMedications} className="sr-only">
            {NA}
          </p>
        </Card>

        {onOpenAllergies ? (
        <AllergiesCard
          items={allergies.items}
          noKnownAllergies={allergies.noKnownAllergies}
          onViewAll={onOpenAllergies}
        />
        ) : null}
      </div>

      <div className="emr-chart-col">
        <Card
          title="Recent Encounters"
          testId={PAT.chartCardEncounters}
          action={
            <RecentEncountersActions
              onNewEncounter={onNewEncounter}
              onViewAll={onOpenEncounters}
            />
          }
        >
          <EncounterTable encounters={encounters} compact onSelect={onSelectEncounter} />
        </Card>

        <Card
          title="Recent Orders / Results"
          action={
            onOpenResults ? <TextLink onClick={onOpenResults}>View all</TextLink> : null
          }
        >
          <EmptyHint>Diagnostic results are not stored on the current patient record.</EmptyHint>
        </Card>

        <Card title="Health Maintenance">
          <EmptyHint>Health-maintenance items are not stored on the current patient record.</EmptyHint>
        </Card>

        <Card title="Vital Signs (Most Recent)">
          <EmptyHint>Vital signs are not stored on the current patient record.</EmptyHint>
        </Card>
      </div>

      <div className="emr-chart-col">
        <Card title="Patient Snapshot" testId={PAT.chartCardFacts}>
          <dl className="emr-chart-dl">
            <Field label="Age">{fields.age != null ? `${fields.age} y/o` : NA}</Field>
            <Field label="Gender">{fields.gender}</Field>
            <Field label="Language">{fields.language}</Field>
            <Field label="Encounters" testId={PAT.chartFactEncounters}>
              {encounters.length}
            </Field>
            {onOpenProblemList ? (
            <Field label="Problems" testId={PAT.chartFactDiagnoses}>
              {problemList.length}
            </Field>
            ) : null}
          </dl>
          {onOpenFrontDesk ? (
          <div className="mt-2">
            <TextLink onClick={onOpenFrontDesk} testId={PAT.chartViewAllDemographics}>
              View demographics
            </TextLink>
          </div>
          ) : null}
        </Card>
        <AiSummaryPanel />
        {onOpenMedicalHistory ? (
        <MedicalHistoryCard
          conditions={medicalHistory.conditions}
          negatives={medicalHistory.negatives}
          onViewAll={onOpenMedicalHistory}
        />
        ) : null}
        {onOpenSurgicalHistory ? (
        <SurgicalHistoryCard
          items={surgicalHistory.items}
          onViewAll={onOpenSurgicalHistory}
        />
        ) : null}
      </div>
    </div>
  );
}

function FrontDeskPanel({ fields, patientId, accessReadOnly }) {
  return (
    <div data-testid={PAT.chartFrontDeskPanel} className="emr-chart-grid emr-chart-grid--two">
      <Card title="Demographics" testId={PAT.chartDemographicsPanel}>
        <dl className="emr-chart-dl">
          <Field label="Name">{fields.name}</Field>
          <Field label="MRN">{fields.mrn}</Field>
          <Field label="Status">{fields.active ? "Active" : "Inactive"}</Field>
          <Field label="Date of Birth">{fields.birthDate ? formatDate(fields.birthDate) : NA}</Field>
          <Field label="Age">{fields.age != null ? `${fields.age} years` : NA}</Field>
          <Field label="Gender">{fields.gender}</Field>
          <Field label="Administrative Gender">{fields.administrativeGender}</Field>
          {fields.ssn ? <Field label="SSN">{fields.ssn}</Field> : null}
          <Field label="Marital Status">{val(fields.marital)}</Field>
          <Field label="Preferred Language">{fields.language}</Field>
          <Field label="Race">{val(fields.race)}</Field>
          <Field label="Ethnicity">{val(fields.ethnicity)}</Field>
          <Field label="Managing Organization">{fields.organization}</Field>
        </dl>
      </Card>
      <div className="emr-chart-col">
        <Card title="Contact Information" testId={PAT.chartCardContact}>
          <dl className="emr-chart-dl">
            <Field label="Phone">{val(fields.phone)}</Field>
            <Field label="Email" testId={PAT.chartEmail}>{val(fields.email)}</Field>
            <Field label="Address" testId={PAT.chartAddress}>{fields.address}</Field>
          </dl>
        </Card>
        <PatientSmsConsentCard patientId={patientId} accessReadOnly={accessReadOnly} />
        <Card title="Emergency Contact" testId={PAT.chartCardEmergency}>
          <dl data-testid={PAT.chartEmergency} className="emr-chart-dl">
            <Field label="Name">{val(fields.contact.name)}</Field>
            <Field label="Relationship">{val(fields.contact.relationship)}</Field>
            <Field label="Phone">{val(fields.contact.phone)}</Field>
            <Field label="Email">{val(fields.contact.email)}</Field>
          </dl>
        </Card>
        <PlaceholderPanel
          title="Insurance"
          detail="Insurance is not available on the current patient record."
          testId={PAT.chartComingSoonInsurance}
        />
        <PlaceholderPanel
          title="Appointments"
          detail="Appointments are not implemented yet."
          testId={PAT.chartComingSoonAppointments}
        />
      </div>
    </div>
  );
}

export function ResultsPanel() {
  return (
    <div data-testid={PAT.chartResultsPanel} className="space-y-4">
      <p className="text-sm text-slate-500">
        Diagnostic results are grouped by type. This is a results workspace, not a catalog of
        orderable labs or radiology studies.
      </p>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {RESULT_TYPES.map((type) => (
          <Card
            key={type}
            title={type}
            testId={`${PAT.chartResultType}-${type.toLowerCase()}`}
          >
            <EmptyHint>No {type.toLowerCase()} results are available for this patient.</EmptyHint>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AiSummaryPanel() {
  return (
    <section data-testid={PAT.chartAiPanel} className="emr-chart-card emr-chart-ai">
      <h2 className="emr-chart-card__title flex items-center gap-2">
        <Sparkles className="h-4 w-4" />
        AI Patient Summary
      </h2>
      <p className="emr-chart-ai__note">
        This panel is reserved for a future AI Patient Summary. It does not analyze patient data
        and does not call an AI service.
      </p>
      <div className="emr-chart-ai__block">
        <h3>Overview</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Current Issues</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Medications</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Allergies</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Recent Results</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Plan</h3>
        <p>Not generated.</p>
      </div>
      <button type="button" disabled className="emr-chart-ai__btn">
        <Sparkles className="h-4 w-4" />
        Generate Summary
      </button>
    </section>
  );
}

export default function PatientChartPage() {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const subtabAccess = user?.subtabAccess;
  const canWritePatients = canWriteMainTab(user?.maintabAccess, "patients");
  const canWriteEncounters = canWriteMainTab(user?.maintabAccess, "encounters");
  const canWriteAppointments = canWriteMainTab(user?.maintabAccess, "appointments");
  const canReadEncounters = canAccessMainTab(user?.maintabAccess, "encounters");
  const canWriteDemographics = canWriteSubtab(subtabAccess, "front_desk", "demographics");
  const canWriteNotes = canWriteSubtab(subtabAccess, "clinical", "notes");
  const canWriteProblems = canWriteSubtab(subtabAccess, "clinical", "problem-list");
  const canWritePmh = canWriteSubtab(subtabAccess, "clinical", "medical-history");
  const canWriteSurgHx = canWriteSubtab(subtabAccess, "clinical", "surgical-history");
  const canWriteAllergies = canWriteSubtab(subtabAccess, "clinical", "allergies");
  const canWriteFdAppointments = canWriteAppointments && canWriteSubtab(subtabAccess, "front_desk", "appointments");
  const canWriteClinAppointments = canWriteAppointments && canWriteSubtab(subtabAccess, "clinical", "appointments");
  const [patient, setPatient] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [domain, setDomain] = useState("clinical");
  const [clinicalTab, setClinicalTab] = useState(() => {
    const tab = searchParams.get("tab");
    if (tab && canAccessSubtab(subtabAccess, "clinical", tab) && CLINICAL_TABS.some((item) => item.id === tab)) {
      return tab;
    }
    return firstAllowedTab(CLINICAL_TABS, "clinical", subtabAccess, "summary");
  });
  const [frontDeskTab, setFrontDeskTab] = useState("demographics");
  const [billingTab, setBillingTab] = useState("charges");
  const [formOpen, setFormOpen] = useState(false);
  const [encounterFormOpen, setEncounterFormOpen] = useState(false);
  const [fhirOpen, setFhirOpen] = useState(false);
  const [problemList, setProblemList] = useState([]);
  const [medicalHistory, setMedicalHistory] = useState({ conditions: [], negatives: [] });
  const [surgicalHistory, setSurgicalHistory] = useState({ items: [] });
  const [allergies, setAllergies] = useState({ items: [], noKnownAllergies: false });

  const frontDeskTabs = useMemo(
    () => filterTabsByAccess(FRONT_DESK_TABS, "front_desk", subtabAccess),
    [subtabAccess],
  );
  const clinicalTabs = useMemo(
    () => filterTabsByAccess(CLINICAL_TABS, "clinical", subtabAccess),
    [subtabAccess],
  );
  const billingTabs = useMemo(
    () => filterTabsByAccess(BILLING_TABS, "billing", subtabAccess),
    [subtabAccess],
  );
  const canReadProblems = canAccessSubtab(subtabAccess, "clinical", "problem-list");
  const canReadPmh = canAccessSubtab(subtabAccess, "clinical", "medical-history");
  const canReadSurgHx = canAccessSubtab(subtabAccess, "clinical", "surgical-history");
  const canReadAllergies = canAccessSubtab(subtabAccess, "clinical", "allergies");
  const canReadClinicalEncounters = canAccessSubtab(subtabAccess, "clinical", "encounters");
  const canReadResults = canAccessSubtab(subtabAccess, "clinical", "results");
  const canReadDemographics = canAccessSubtab(subtabAccess, "front_desk", "demographics");
  const visibleDomains = useMemo(() => {
    const ids = [];
    if (frontDeskTabs.length) ids.push("front-desk");
    if (clinicalTabs.length) ids.push("clinical");
    if (billingTabs.length) ids.push("billing");
    return ids;
  }, [frontDeskTabs, clinicalTabs, billingTabs]);

  useEffect(() => {
    setFrontDeskTab((current) =>
      firstAllowedTab(FRONT_DESK_TABS, "front_desk", subtabAccess, current),
    );
    setClinicalTab((current) =>
      firstAllowedTab(CLINICAL_TABS, "clinical", subtabAccess, current),
    );
    setBillingTab((current) =>
      firstAllowedTab(BILLING_TABS, "billing", subtabAccess, current),
    );
  }, [subtabAccess]);

  useEffect(() => {
    if (visibleDomains.length && !visibleDomains.includes(domain)) {
      setDomain(visibleDomains[0]);
    }
  }, [visibleDomains, domain]);

  const openClinicalTab = (tabId) => {
    if (!clinicalTabs.some((tab) => tab.id === tabId)) return;
    setClinicalTab(tabId);
  };

  const closeToAllowedClinical = () => {
    setClinicalTab((current) => firstAllowedTab(CLINICAL_TABS, "clinical", subtabAccess, "summary") || current);
  };

  const openEncounter = (enc, noteId) => {
    const path = encounterScreenPath(enc, noteId ? { noteId } : {});
    if (path) navigate(path);
  };

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const [pat, encData] = await Promise.all([
        fetchPatient(patientId),
        canReadEncounters ? fetchPatientEncounters(patientId) : Promise.resolve({ items: [] }),
      ]);
      const items = sortEncountersNewest(encData.items || []);
      setPatient(pat);
      setEncounters(items);
    } catch (err) {
      const status = err?.response?.status;
      setPatient(null);
      setEncounters([]);
      setError(status === 404 ? "Patient not found." : "Failed to load patient chart.");
      if (status !== 404) toast.error("Failed to load patient chart.");
    } finally {
      setLoading(false);
    }
  }, [patientId, canReadEncounters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setProblemList([]);
    setMedicalHistory({ conditions: [], negatives: [] });
    setSurgicalHistory({ items: [] });
    setAllergies({ items: [], noKnownAllergies: false });
    if (canReadProblems) {
      loadSummaryProblemList(patientId)
        .then((items) => {
          if (!cancelled) setProblemList(items);
        })
        .catch(() => {
          if (!cancelled) setProblemList([]);
        });
    }
    if (canReadPmh) {
      loadSummaryMedicalHistory(patientId)
        .then((data) => {
          if (!cancelled) setMedicalHistory(data);
        })
        .catch(() => {
          if (!cancelled) setMedicalHistory({ conditions: [], negatives: [] });
        });
    }
    if (canReadSurgHx) {
      loadSummarySurgicalHistory(patientId)
        .then((data) => {
          if (!cancelled) setSurgicalHistory(data);
        })
        .catch(() => {
          if (!cancelled) setSurgicalHistory({ items: [] });
        });
    }
    if (canReadAllergies) {
      loadSummaryAllergies(patientId)
        .then((data) => {
          if (!cancelled) setAllergies(data);
        })
        .catch(() => {
          if (!cancelled) setAllergies({ items: [], noKnownAllergies: false });
        });
    }
    return () => {
      cancelled = true;
    };
  }, [patientId, clinicalTab, canReadProblems, canReadPmh, canReadSurgHx, canReadAllergies]);

  const fields = useMemo(() => (patient ? chartPatientFields(patient) : null), [patient]);

  if (loading) {
    return (
      <div data-testid={PAT.chartPage} className="emr-chart py-10 text-center text-sm text-slate-400">
        Loading patient chart…
      </div>
    );
  }

  if (error || !fields) {
    return (
      <div data-testid={PAT.chartPage} className="emr-chart mx-auto max-w-lg p-6 text-center">
        <div className="emr-chart-card p-8">
          <p className="text-sm font-medium text-slate-800">{error || "Patient not found."}</p>
          <Link
            to="/patients"
            data-testid={PAT.chartBack}
            className="emr-chart-back mt-4"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back to Patients
          </Link>
        </div>
      </div>
    );
  }

  const dobLabel = fields.birthDate
    ? `${formatDate(fields.birthDate)}${fields.age != null ? ` (${fields.age} y/o)` : ""}`
    : NA;

  return (
    <div data-testid={PAT.chartPage} className="emr-chart">
      <header data-testid={PAT.chartHeader} className="emr-chart-header">
        <Link to="/patients" data-testid={PAT.chartBack} className="emr-chart-back">
          <ChevronLeft className="h-4 w-4" />
          Back to Patient List
        </Link>
        <div className="emr-chart-header__row">
          <div className="emr-chart-header__identity">
            <div className="emr-chart-avatar" aria-hidden="true">{fields.initials}</div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div data-testid={PAT.chartName} className="emr-chart-name truncate">
                  {fields.name}
                </div>
                <span
                  data-testid={PAT.chartStatus}
                  className={fields.active ? "emr-chart-status is-active" : "emr-chart-status is-inactive"}
                >
                  {fields.active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="emr-chart-meta">
                <span>MRN {fields.mrn || NA}</span>
                <span>DOB {dobLabel}</span>
                <span>Gender {fields.gender}</span>
                <span data-testid={PAT.chartPhone}>Phone {val(fields.phone)}</span>
                {fields.email ? <span>Email {fields.email}</span> : null}
              </div>
            </div>
          </div>
          <div className="emr-chart-actions">
            <Button
              variant="outline"
              className="emr-chart-btn"
              onClick={() => toast.info("Print is not available yet.")}
            >
              <Printer className="h-4 w-4" />
              Print
            </Button>
            <Button
              variant="outline"
              className="emr-chart-btn"
              onClick={() => toast.info("Messaging is not available yet.")}
            >
              <MessageSquare className="h-4 w-4" />
              Message
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="emr-chart-btn"
                  data-testid={PAT.chartMoreBtn}
                >
                  More
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="emr-chart-popper">
                {canWritePatients ? (
                <DropdownMenuItem data-testid={PAT.chartEditBtn} onClick={() => setFormOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit Patient
                </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onClick={() => setFhirOpen(true)}>
                  <Code2 className="mr-2 h-4 w-4" />
                  View FHIR
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <MoreHorizontal className="mr-2 h-4 w-4" />
                  More actions coming soon
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <p data-testid={PAT.chartSubtitle} className="sr-only">
          {[fields.mrn, fields.age != null ? `${fields.age} y/o` : null, fields.gender]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </header>

      <div className="chart-folder emr-chart-nav">
      <div className="chart-folder-nav">
        <ChartDomainDividers activeId={domain} onSelect={setDomain} visibleIds={visibleDomains} />
        {domain === "front-desk" && (
          <FrontDeskSubtabs
            tabs={frontDeskTabs}
            activeId={frontDeskTab}
            onSelect={setFrontDeskTab}
          />
        )}
        {domain === "clinical" && (
          <ClinicalSubtabs
            tabs={clinicalTabs}
            activeId={clinicalTab}
            onSelect={setClinicalTab}
          />
        )}
        {domain === "billing" && (
          <BillingSubtabs
            tabs={billingTabs}
            activeId={billingTab}
            onSelect={setBillingTab}
          />
        )}
      </div>

      <div data-testid={PAT.chartWorkspace} className="emr-chart-workspace">
        <div className="min-w-0">
          {domain === "front-desk" && frontDeskTab === "demographics" && (
            <FrontDeskPanel fields={fields} patientId={patientId} accessReadOnly={!canWriteDemographics} />
          )}

          {domain === "front-desk" && frontDeskTab === "insurance" && (
            <PlaceholderPanel
              title="Insurance"
              detail="Insurance is not available on the current patient record."
              testId={PAT.chartComingSoonInsurance}
            />
          )}

          {domain === "front-desk" && frontDeskTab === "appointments" && (
            <AppointmentsWorkspace
              patientId={patientId}
              patientName={fields.name}
              encounters={encounters}
              accessReadOnly={!canWriteFdAppointments}
              canWriteEncounters={canWriteEncounters}
              canAccessEncounters={canReadEncounters}
            />
          )}

          {domain === "billing" && (
            <PlaceholderPanel
              title={BILLING_TABS.find((tab) => tab.id === billingTab)?.label || "Billing"}
              detail="Charges, claims, and payments are not implemented yet. This domain is reserved for future billing workflows."
              testId={PAT.chartBillingPanel}
            />
          )}

          {domain === "clinical" && (
            <div className="space-y-4">
              {clinicalTab === "summary" && (
                <ClinicalSummary
                  fields={fields}
                  problemList={problemList}
                  medicalHistory={medicalHistory}
                  surgicalHistory={surgicalHistory}
                  allergies={allergies}
                  encounters={encounters}
                  onOpenFrontDesk={
                    canReadDemographics
                      ? () => {
                          setDomain("front-desk");
                          setFrontDeskTab("demographics");
                        }
                      : undefined
                  }
                  onOpenEncounters={canReadClinicalEncounters ? () => openClinicalTab("encounters") : undefined}
                  onNewEncounter={canWritePatients && canWriteEncounters ? () => setEncounterFormOpen(true) : undefined}
                  onOpenResults={canReadResults ? () => openClinicalTab("results") : undefined}
                  onOpenMedicalHistory={canReadPmh ? () => openClinicalTab("medical-history") : undefined}
                  onOpenSurgicalHistory={canReadSurgHx ? () => openClinicalTab("surgical-history") : undefined}
                  onOpenAllergies={canReadAllergies ? () => openClinicalTab("allergies") : undefined}
                  onOpenProblemList={canReadProblems ? () => openClinicalTab("problem-list") : undefined}
                  onSelectEncounter={openEncounter}
                />
              )}

              {clinicalTab === "problem-list" && (
                <ProblemListWorkspace patientId={patientId} onChange={setProblemList} accessReadOnly={!canWriteProblems} />
              )}

              {clinicalTab === "medical-history" && (
                <MedicalHistoryWorkspace
                  patientId={patientId}
                  birthDate={fields.birthDate}
                  onClose={closeToAllowedClinical}
                  onPrevious={() => setClinicalTab(cycleVisibleTab(clinicalTabs, clinicalTab, -1))}
                  onNext={() => setClinicalTab(cycleVisibleTab(clinicalTabs, clinicalTab, 1))}
                  accessReadOnly={!canWritePmh}
                />
              )}

              {clinicalTab === "surgical-history" && (
                <SurgicalHistoryWorkspace
                  patientId={patientId}
                  patient={fields}
                  currentUser={user}
                  onClose={closeToAllowedClinical}
                  onPrevious={() => setClinicalTab(cycleVisibleTab(clinicalTabs, clinicalTab, -1))}
                  onNext={() => setClinicalTab(cycleVisibleTab(clinicalTabs, clinicalTab, 1))}
                  onChange={setSurgicalHistory}
                  accessReadOnly={!canWriteSurgHx}
                />
              )}

              {clinicalTab === "encounters" && (
                <EncountersWorkspace encounters={encounters} onSelect={openEncounter} />
              )}

              {clinicalTab === "appointments" && (
                <AppointmentsWorkspace
                  patientId={patientId}
                  patientName={fields.name}
                  encounters={encounters}
                  accessReadOnly={!canWriteClinAppointments}
                  canWriteEncounters={canWriteEncounters}
                  canAccessEncounters={canReadEncounters}
                />
              )}

              {clinicalTab === "notes" && (
                <NotesWorkspace
                  patientId={patientId}
                  encounters={encounters}
                  currentUser={user}
                  onOpenNote={(note) => openEncounter({ id: note.encounterId }, note.id)}
                  accessReadOnly={!canWriteNotes}
                />
              )}

              {clinicalTab === "results" && <ResultsPanel />}

              {clinicalTab === "medications" && (
                <Card title="Medications (Active)" testId={PAT.chartComingSoonMedications}>
                  <EmptyHint>No patient-specific medications are available yet.</EmptyHint>
                  <p data-testid={PAT.chartFactMedications} className="sr-only">
                    {NA}
                  </p>
                </Card>
              )}

              {clinicalTab === "allergies" && (
                <AllergiesWorkspace
                  patientId={patientId}
                  patient={fields}
                  currentUser={user}
                  onClose={closeToAllowedClinical}
                  onChange={setAllergies}
                  accessReadOnly={!canWriteAllergies}
                />
              )}

              {clinicalTab === "miscellaneous" && (
                <div data-testid={PAT.chartMiscPanel} className="space-y-4">
                  <p className="text-sm text-slate-500">
                    Clinical information that does not belong in Summary, Encounters, Notes, or
                    Results can be added here later. This section is intentionally small and
                    extensible.
                  </p>
                  <PlaceholderPanel
                    title="Documents"
                    detail="Patient documents are not implemented yet."
                    testId={PAT.chartComingSoonDocuments}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      <footer data-testid={PAT.chartFooter} className="sr-only">
        Patient chart
      </footer>

      <PatientForm
        open={formOpen}
        editing={patient}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          load();
        }}
        contentClassName="emr-chart-dialog"
      />
      <EncounterForm
        open={encounterFormOpen}
        presetPatientId={patientId}
        onClose={() => setEncounterFormOpen(false)}
        onSaved={(created) => {
          setEncounterFormOpen(false);
          const path = encounterScreenPath(created);
          if (path) navigate(path);
        }}
      />
      <FhirViewer open={fhirOpen} resource={patient} onClose={() => setFhirOpen(false)} />
    </div>
  );
}
